import React, {useEffect, useRef, useState} from 'react';
import {
	Alert,
	Avatar,
	Box,
	Grow,
	Typography,
	Link,
	Button,
	Card,
	CardActions,
	CardMedia,
	CircularProgress,
	TextField
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import QeeFileInput from '../../../../qeeFileInput';
import { QeeColorButtonPrimary, QeeColorButtonSecondary } from '../../../../qeeColorButton';
import {ChatImageGallery} from './ChatImageGallery';
import {ChatAssistantAvatar} from './ChatAssistantAvatar';
import {LeylaBossMoment} from './LeylaBossMoment';
import {dispatchAssistantAvatar, resolveAssistantAvatarLabel} from './chatAssistantAvatarDispatcher';
import {resolveChatUiLabels} from './chatUiLabels';

export function MuiMessage({
	chatController,
	id,
	message,
	showDate,
	showTime,
	showSupportActions=false,
	onSupportRequest,
	onGuruPdfDownload,
	onGuruExcelDownload,
	supportLanguage='',
	supportUserEmail='',
	forceSupportCard=0,
	forceSupportNeedsEmail=false,
	messageIndex=-1,
	messages=[],
	enableLeylaBoss=true,
	leylaBossCooldown=3,
	assistantAvatarLabels={},
	chatUiLabels={}
}) {
	const [feedbackGiven, setFeedbackGiven] = useState(false);
	const [showSupportCard, setShowSupportCard] = useState(Boolean(message.offerHumanSupport));
	const [additionalMessage, setAdditionalMessage] = useState('');
	const [visitorEmail, setVisitorEmail] = useState('');
	const [emailTouched, setEmailTouched] = useState(false);
	const [messageTouched, setMessageTouched] = useState(false);
	const [sending, setSending] = useState(false);
	const [sentResult, setSentResult] = useState(null);
	const [supportError, setSupportError] = useState('');
	const [supportScrollRequest, setSupportScrollRequest] = useState(0);
	const [pdfDownloading, setPdfDownloading] = useState(false);
	const [pdfDownloadError, setPdfDownloadError] = useState('');
	const [excelDownloading, setExcelDownloading] = useState(false);
	const [excelDownloadError, setExcelDownloadError] = useState('');
	const supportCardRef = useRef(null);
	const uiLabels = resolveChatUiLabels(supportLanguage, chatUiLabels);
	const labels = {
		...uiLabels,
		title: uiLabels.supportTitle,
		description: uiLabels.supportDescription,
		transmitted: uiLabels.supportTransmitted,
		send: uiLabels.supportSend,
		failed: uiLabels.supportFailed
	};
	const supportEnabled = Boolean(onSupportRequest) && showSupportActions && !message.self &&
		(Boolean(message.helpUsed) || Boolean(message.offerHumanSupport) || forceSupportCard > 0);
	const emailRequired = Boolean(message.supportNeedsEmail) || Boolean(forceSupportNeedsEmail);
	const validVisitorEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(visitorEmail.trim());
	const validAdditionalMessage = additionalMessage.trim().length >= 5;
	const embeddedPdfMatch = !message.self && typeof message.content === 'string'
		? message.content.match(/\{GuruPdf\|([a-f0-9]{32})\|([^{}|]+)\}/i)
		: null;
	let embeddedPdfDownload = null;
	if (embeddedPdfMatch) {
		try {
			const embeddedFilename = decodeURIComponent(embeddedPdfMatch[2]);
			if (/\.pdf$/i.test(embeddedFilename) && !/[\x00-\x1f\x7f\\/]/.test(embeddedFilename)) {
				embeddedPdfDownload = {
					fileId: embeddedPdfMatch[1].toLowerCase(),
					filename: embeddedFilename.slice(0, 160),
					size: 0
				};
			}
		} catch (error) {
			embeddedPdfDownload = null;
		}
	}
	const structuredPdfDownload = !message.self && message.guruPdfDownload &&
		/^[a-f0-9]{32}$/i.test(String(message.guruPdfDownload.fileId || '')) &&
		/\.pdf$/i.test(String(message.guruPdfDownload.filename || '')) &&
		!/[\x00-\x1f\x7f\\/]/.test(String(message.guruPdfDownload.filename || ''))
		? message.guruPdfDownload : null;
	const pdfDownload = structuredPdfDownload || embeddedPdfDownload;
	const embeddedExcelMatch = !message.self && typeof message.content === 'string'
		? message.content.match(/\{GuruExcel\|([a-f0-9]{32})\|([^{}|]+)\}/i) : null;
	let embeddedExcelDownload = null;
	if (embeddedExcelMatch) {
		try {
			const filename = decodeURIComponent(embeddedExcelMatch[2]);
			if (/\.xlsx$/i.test(filename) && !/[\x00-\x1f\x7f\\/]/.test(filename)) {
				embeddedExcelDownload = {fileId: embeddedExcelMatch[1].toLowerCase(), filename: filename.slice(0, 160), size: 0};
			}
		} catch (error) { embeddedExcelDownload = null; }
	}
	const structuredExcelDownload = !message.self && message.guruExcelDownload &&
		/^[a-f0-9]{32}$/i.test(String(message.guruExcelDownload.fileId || '')) &&
		/\.xlsx$/i.test(String(message.guruExcelDownload.filename || '')) &&
		!/[\x00-\x1f\x7f\\/]/.test(String(message.guruExcelDownload.filename || ''))
		? message.guruExcelDownload : null;
	const excelDownload = structuredExcelDownload || embeddedExcelDownload;
	const visibleMessageContent = typeof message.content === 'string'
		? message.content
			.replace(/(?:\r?\n\s*)*\{GuruPdf\|[a-f0-9]{32}\|[^{}|]+\}/ig, '')
			.replace(/(?:\r?\n\s*)*\{GuruExcel\|[a-f0-9]{32}\|[^{}|]+\}/ig, '').trimEnd()
		: message.content;
	useEffect(() => {
		if (forceSupportCard > 0) {
			setFeedbackGiven(true);
			setShowSupportCard(true);
			setSupportError('');
			setSupportScrollRequest(value => value + 1);
		}
	}, [forceSupportCard]);

	useEffect(() => {
		if (!showSupportCard || supportScrollRequest <= 0) return undefined;
		const scrollTimer = window.setTimeout(() => {
			if (supportCardRef.current) {
				supportCardRef.current.scrollIntoView({behavior: 'smooth', block: 'end'});
			}
		}, 0);
		return () => window.clearTimeout(scrollTimer);
	}, [showSupportCard, supportScrollRequest]);

	const openSupportCard = () => {
		setFeedbackGiven(true);
		setShowSupportCard(true);
		setSupportError('');
		setSupportScrollRequest(value => value + 1);
	};

	const submitSupportRequest = async () => {
		setMessageTouched(true);
		if (emailRequired) setEmailTouched(true);
		if (!validAdditionalMessage) {
			setSupportError(labels.messageRequired);
			return;
		}
		if (emailRequired && !validVisitorEmail) {
			setSupportError(labels.invalidEmail);
			return;
		}
		setSending(true);
		setSupportError('');
		try {
			const result = await onSupportRequest({message, additionalMessage, visitorEmail});
			const resultConfirmed = result && (result.sent === true ||
				String(result.sent).toLowerCase() === 'true' || String(result.sent) === '1');
			if (!resultConfirmed) {
				throw new Error((result && result.error) || labels.failed);
			}
			setSentResult(result);
		} catch (error) {
			setSupportError((error.response && error.response.data && error.response.data.error) || error.message || labels.failed);
		} finally {
			setSending(false);
		}
	};
	const downloadPdf = async () => {
		if (!pdfDownload || pdfDownloading || typeof onGuruPdfDownload !== 'function') return;
		setPdfDownloading(true);
		setPdfDownloadError('');
		try {
			await onGuruPdfDownload(pdfDownload);
		} catch (error) {
			setPdfDownloadError(uiLabels.downloadPdfError);
		} finally {
			setPdfDownloading(false);
		}
	};
	const downloadExcel = async () => {
		if (!excelDownload || excelDownloading || typeof onGuruExcelDownload !== 'function') return;
		setExcelDownloading(true);
		setExcelDownloadError('');
		try { await onGuruExcelDownload(excelDownload); }
		catch (error) { setExcelDownloadError(uiLabels.downloadExcelError); }
		finally { setExcelDownloading(false); }
	};
	if (message.deletedAt) {
		return <div id={id} />;
	}

	const _prepareLinkHref = (href) => {
		try {
			const targetUrl = new URL(String(href || ''), window.location.href);
			if (targetUrl.origin === window.location.origin &&
				targetUrl.pathname.toLowerCase().indexOf('qeeleaembed.html') === -1) {
				targetUrl.searchParams.set('synapcusGuruTarget', '1');
				return targetUrl.toString();
			}
		} catch (error) {
			// Keep malformed or non-HTTP links unchanged; the browser remains the
			// authority for handling them.
		}
		return href;
	};

	const _parseLinksToJSX = (subText) => {
		const linkRegex = /\{Link\|(.*?)\|(.*?)\}/g;
		let elements = [];
		let lastIndex = 0;
		let match;

		while ((match = linkRegex.exec(subText)) !== null) {
			const [fullMatch, href, linkText] = match;
			const start = match.index;

			if (start > lastIndex) {
				elements.push(subText.substring(lastIndex, start));
			}

			const preparedHref = _prepareLinkHref(href);
			elements.push(
				<Link href={preparedHref} target="_blank" rel="noopener noreferrer" key={href}>
					{linkText}<OpenInNewIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
				</Link>
			);

			lastIndex = start + fullMatch.length;
		}

		if (lastIndex < subText.length) {
			elements.push(subText.substring(lastIndex));
		}

		return elements;
	};

	const _parseImgGalleryToJSX = (subText) => {
		const galleryRegex = /\{ImgGallery\|([^|]+)\|([^|]+)\|([^}]+)\}/;
		const match = subText.match(galleryRegex);

		if (!match) return [];

		const [_, imgStr, linkStr, titleStr] = match;
		const imgs = imgStr.split(',');
		const links = linkStr.split(',');
		const titles = titleStr.split(',');

		const elements = imgs.map((img, index) => {
			const link = links[index] || '';
			const title = titles[index] || '';

			return (
				<Box 
					key={img} 
					style={{ 
						marginRight: '8px', 
						borderRadius: '8px', 
						overflow: 'hidden', 
						display: 'inline-block'
					}}
				>
					<Card 
						variant="outlined" 
						style={{ 
							width: '150px',
							borderRadius: '8px',
						}}
					>
						<CardMedia
							component="img"
							image={img}
							alt={title}
							title={title}
							style={{ 
								height: '150px', 
								objectFit: 'cover',
							}}
						/>
						<CardActions style={{ padding: 0, display: 'block' }}>
							<Button
								href={link}
								target="_blank"
								rel="noopener noreferrer"
								size="small"
								color="primary"
								style={{ 
									width: '100%', 
									borderRadius: 0,
								}}
							>
								{title || 'View'}
							</Button>
						</CardActions>
					</Card>
				</Box>
			);
		});

		return (
			<Box display="flex" overflowX="auto" padding="0px" marginY="8px">
				{elements}
			</Box>
		);
	};

	const _parseContentToJSX = (text) => {
		let content = [];
		const splitText = text.split('{br}');
		
		splitText.forEach((part, index) => {
			if (index > 0) {
				content.push(<br key={`br-${index}`} />);
			}
			let galleryMatch = part.match(/\{ImgGallery\|[^}]+\}/);
			if (galleryMatch) {
				const beforeGallery = part.substring(0, galleryMatch.index);
				const afterGallery = part.substring(galleryMatch.index + galleryMatch[0].length);

				content = content.concat(_parseLinksToJSX(beforeGallery), _parseImgGalleryToJSX(galleryMatch[0]), _parseLinksToJSX(afterGallery));
			} else {
				content = content.concat(_parseLinksToJSX(part));
			}
		});

		return content;
	};

	const dispDate = message.updatedAt ? message.updatedAt : message.createdAt;
	const presentation = dispatchAssistantAvatar({
		message,
		messageIndex,
		messages,
		enabled: enableLeylaBoss,
		cooldown: leylaBossCooldown
	});
	const avatarLocale = String(supportLanguage || '').toLowerCase();
	const avatarLanguage = avatarLocale.split(/[-_]/)[0];
	const configuredAvatarLabels = assistantAvatarLabels && typeof assistantAvatarLabels[avatarLocale] === 'object'
		? assistantAvatarLabels[avatarLocale]
		: assistantAvatarLabels && typeof assistantAvatarLabels[avatarLanguage] === 'object'
			? assistantAvatarLabels[avatarLanguage]
			: assistantAvatarLabels;
	const avatarTooltip = resolveAssistantAvatarLabel({
		language: supportLanguage,
		key: presentation.tooltipKey,
		overrides: {
			onlineHelp: uiLabels.avatarOnlineHelp,
			prj: uiLabels.avatarPrj,
			hr: uiLabels.avatarHr,
			crm: uiLabels.avatarCrm,
			address: uiLabels.avatarAddress,
			general: uiLabels.avatarGeneral,
			synapcus: uiLabels.avatarSynapcus,
			...(configuredAvatarLabels || {})
		}
	});
	const hasExternalAvatar = !message.self && typeof message.avatar === 'string' &&
		message.avatar.trim() !== '' && message.avatar !== 'AVATAR';

	const ChatAvatar = (
		<Box
			minWidth={0}
			flexShrink={0}
			ml={message.self ? 1 : 0}
			mr={message.self ? 0 : 1}
			sx={{ display: { xs: 'none', sm: 'block' } }}
		>
			{!message.self && !hasExternalAvatar ? (
				<ChatAssistantAvatar assetKey={presentation.assetKey} tooltip={avatarTooltip} viewerLabels={labels} />
			) : (
				<Avatar alt={message.perName} src={message.avatar} />
			)}
		</Box>
	);

	const ChatUsername = (
		<Box maxWidth="100%" mx={1}>
			<Typography variant="body2" align={message.self ? 'right' : 'left'}>
				{message.perName}
			</Typography>
		</Box>
	);

	const ChatDate = (
		<Box maxWidth="100%" mx={1}>
			<Typography variant="body2" align={message.self ? 'right' : 'left'} color="textSecondary">
				{dispDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
			</Typography>
		</Box>
	);

	const ChatUsernameDate = ({ message, dispDate }) => {
		return (
			<Box 
				maxWidth="100%" 
				mx={1} 
				style={{ 
					whiteSpace: 'nowrap', 
					display: 'flex', 
					alignItems: 'center', 
					justifyContent: message.self ? 'flex-end' : 'flex-start'
				}}
			>
				<Box>
					<Typography 
						variant="body2" 
						align={message.self ? 'right' : 'left'}
						color="textSecondary" 
						style={{ fontSize: '80%', fontWeight: 600 }}
					>
						{message.perName}
					</Typography>
				</Box>
				<Box mx={1}>
					<Typography 
						variant="body2" 
						align={message.self ? 'right' : 'left'}
						color="textSecondary" 
						style={{ fontSize: '80%' }}
					>
						{dispDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
					</Typography>
				</Box>
			</Box>
		);
	};

	return (
		<Grow in>
			<Box maxWidth="100%" display="flex" flexDirection="column" sx={{ pt: { xs: 0.25, sm: 1 } }}>
				{showDate && (
					<Typography align="center" style={{ fontWeight: 'bold' }}>
						{dispDate?.toLocaleDateString()}
					</Typography>
				)}
				<Box
					id={id}
					maxWidth="100%"
					my={{ xs: 0.5, sm: 1 }}
					pl={message.self ? { xs: '2%', sm: '4%' } : 0}
					pr={message.self ? 0 : { xs: 0, sm: '2%' }}
					display="flex"
					justifyContent={message.self ? 'flex-end' : 'flex-start'}
					style={{ overflowWrap: 'break-word' }}
				>
					{!message.self && ChatAvatar}
					<Box
						minWidth={0}
						maxWidth={message.self ? '60%' : '100%'}
						display="flex"
						flexDirection="column"
					>
						{showTime && <ChatUsernameDate message={message} dispDate={dispDate} />}
						<Box
							maxWidth="100%"
							py={{ xs: 0.85, sm: 1 }}
							px={{ xs: 1.25, sm: 2 }}
							bgcolor={message.self ? 'primary.main' : 'background.paper'}
							color={message.self ? 'primary.contrastText' : 'text.primary'}
							borderRadius={4}
							boxShadow={2}
							mt={0.1}
							className={message.self ? "MuiMessageSelf" : "MuiMessage"}
							sx={{
								overflowWrap: 'anywhere',
								wordBreak: 'break-word',
								'@media (max-width:599.95px) and (hover:none) and (pointer:coarse)': {
									fontSize: '1.25rem'
								}
							}}
						>
							{message.type === 'text' && (
								<Typography variant="body1" sx={{
									whiteSpace: 'pre-wrap',
									color: message.self ? '#fff' : '#222',
									'@media (max-width:599.95px) and (hover:none) and (pointer:coarse)': {
										fontSize: '1.25rem'
									}
								}}>
									{_parseContentToJSX(visibleMessageContent)}
								</Typography>
							)}
							{message.type === 'jsx' && _parseLinksToJSX(message.content)}
							{pdfDownload && (
								<Box
									role="button"
									tabIndex={0}
									aria-label={uiLabels.downloadPdf + ': ' + pdfDownload.filename}
									title={pdfDownload.filename}
									onClick={downloadPdf}
									onKeyDown={event => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											downloadPdf();
										}
									}}
									sx={{
										mt: 1.25,
										p: 1.25,
										display: 'flex',
										alignItems: 'center',
										gap: 1.25,
										maxWidth: 420,
										border: 1,
										borderColor: '#b7d7c5',
										borderRadius: 2,
										bgcolor: '#f4faf7',
										cursor: pdfDownloading ? 'wait' : 'pointer',
										transition: 'background-color 120ms ease, border-color 120ms ease',
										'&:hover, &:focus-visible': {
											bgcolor: '#e5f4eb',
											borderColor: 'var(--primary-color, #004c3f)',
											outline: 'none'
										}
									}}
								>
										<PictureAsPdfOutlinedIcon sx={{fontSize: 42, flexShrink: 0, color: 'var(--primary-color, #004c3f)'}} />
									<Box sx={{minWidth: 0, flex: 1}}>
										<Typography variant="body2" fontWeight={700} sx={{
											whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
										}}>
											{pdfDownload.filename}
										</Typography>
									<Typography variant="caption" sx={{color: 'var(--primary-color, #004c3f)'}}>
											{pdfDownloading ? uiLabels.downloadingPdf : uiLabels.downloadPdf}
										</Typography>
									</Box>
									{pdfDownloading
										? <CircularProgress size={22} sx={{color: 'var(--primary-color, #004c3f)'}} />
										: <DownloadOutlinedIcon sx={{color: 'var(--primary-color, #004c3f)'}} />}
								</Box>
							)}
							{pdfDownloadError && <Alert severity="error" sx={{mt: 1}}>{pdfDownloadError}</Alert>}
							{excelDownload && (
								<Box role="button" tabIndex={0}
									aria-label={uiLabels.downloadExcel + ': ' + excelDownload.filename}
									title={excelDownload.filename} onClick={downloadExcel}
									onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); downloadExcel(); } }}
									sx={{mt: 1.25, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25,
										maxWidth: 420, border: 1, borderColor: '#b7d7c5', borderRadius: 2,
										bgcolor: '#f3faf6', cursor: excelDownloading ? 'wait' : 'pointer',
										transition: 'background-color 120ms ease, border-color 120ms ease',
										'&:hover, &:focus-visible': {bgcolor: '#e5f4eb', borderColor: '#107c41', outline: 'none'}}}>
									<GridOnOutlinedIcon sx={{fontSize: 42, flexShrink: 0, color: '#107c41'}} />
									<Box sx={{minWidth: 0, flex: 1}}>
										<Typography variant="body2" fontWeight={700} sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
											{excelDownload.filename}
										</Typography>
										<Typography variant="caption" sx={{color: '#107c41'}}>
											{excelDownloading ? uiLabels.downloadingExcel : uiLabels.downloadExcel}
										</Typography>
									</Box>
									{excelDownloading ? <CircularProgress size={22} sx={{color: '#107c41'}} /> : <DownloadOutlinedIcon sx={{color: '#107c41'}} />}
								</Box>
							)}
							{excelDownloadError && <Alert severity="error" sx={{mt: 1}}>{excelDownloadError}</Alert>}
							{message.type === 'file' && (	
								<>
								{/*'message.uploadId: ' + message.uploadId*/}	
								<QeeFileInput
									facId={chatController.parentForm + '-' + chatController.parentId}
									username={message.username}
									buttonText=""
									keyItm={'upload_id'}
									keyVal={'"'+message.uploadId+'"'}
									hideUploadButton={true}
									showFileList={true}
								/>
								</>
							)}
							{!message.self ? <ChatImageGallery images={message.images} labels={labels} /> : null}
						</Box>
						{!message.self ? <LeylaBossMoment moment={presentation.endingMoment} /> : null}
						{supportEnabled && (
							<Box mt={1} maxWidth={620}>
								{sentResult ? (
									<Alert severity="success">
										{labels.sent} <strong>{sentResult.copiedTo || visitorEmail || supportUserEmail}</strong>.
									</Alert>
								) : showSupportCard ? (
									<Card
										ref={supportCardRef}
										variant="outlined"
										sx={{
											scrollMarginBottom: 16,
											p: {xs: 1.5, sm: 2},
											borderColor: 'divider',
											borderRadius: 3,
											bgcolor: 'background.paper',
											boxShadow: 1
										}}
									>
										<Box sx={{borderLeft: 4, borderColor: 'primary.main', pl: 1.5}}>
											<Typography variant="subtitle1" color="text.primary" fontWeight={700}>
												{labels.title}
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
												{labels.description}
											</Typography>
										</Box>
										<Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1.5}}>
											{labels.transmitted}
										</Typography>
										{emailRequired ? (
											<TextField fullWidth required size="small" type="email" label={labels.email}
												value={visitorEmail}
												onChange={event => { setVisitorEmail(event.target.value); setSupportError(''); }}
												onBlur={() => setEmailTouched(true)}
												error={(emailTouched || visitorEmail.length > 0) && !validVisitorEmail}
												helperText={!validVisitorEmail ? labels.invalidEmail : ' '}
												inputProps={{autoComplete: 'email'}}
												sx={{mt: 1.5}} />
										) : supportUserEmail ? (
											<Typography variant="body2" sx={{mt: 1}}>{labels.email}: <strong>{supportUserEmail}</strong></Typography>
										) : null}
										<TextField fullWidth required multiline minRows={2} size="small" label={labels.note}
											value={additionalMessage}
											onChange={event => { setAdditionalMessage(event.target.value); setSupportError(''); }}
											onBlur={() => setMessageTouched(true)}
											error={(messageTouched || additionalMessage.length > 0) && !validAdditionalMessage}
											helperText={!validAdditionalMessage ? labels.messageRequired : ' '}
											inputProps={{minLength: 5, maxLength: 2000}}
											sx={{mt: 1.5}} />
										{supportError && <Alert severity="error" sx={{mt: 1}}>{supportError}</Alert>}
										<Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1} mt={2}>
											<QeeColorButtonSecondary
												size="small"
												className="qeeSupportSecondaryButton"
												onClick={() => setShowSupportCard(false)}
												disabled={sending}
												sx={{
													mr: 0,
													'&&': {
														backgroundColor: '#fff',
														color: 'var(--primary-color, #004c3f)',
														border: '1px solid var(--primary-color, #004c3f)'
													},
													'&&:hover': {
														backgroundColor: 'rgba(0, 76, 63, 0.08)'
													}
												}}
											>
												{labels.cancel}
											</QeeColorButtonSecondary>
											<QeeColorButtonPrimary
												size="small"
												disableElevation
												onClick={submitSupportRequest}
												disabled={sending || !validAdditionalMessage || (emailRequired && !validVisitorEmail)}
												sx={{
													fontWeight: 600,
													mr: 0,
													'&&:not(.Mui-disabled)': {
														backgroundColor: 'var(--primary-color, #004c3f)',
														color: '#fff'
													},
													'&&:not(.Mui-disabled):hover': {
														filter: 'brightness(0.9)'
													}
												}}
											>
												{sending ? labels.sending : labels.send}
											</QeeColorButtonPrimary>
										</Box>
									</Card>
								) : !feedbackGiven && message.helpUsed ? (
									<Box display="flex" gap={1}>
										<QeeColorButtonSecondary
											size="small"
											variant="outlined"
											startIcon={<ThumbUpAltOutlinedIcon />}
											onClick={() => setFeedbackGiven(true)}
											sx={{mr: 0}}
										>
											{labels.helpful}
										</QeeColorButtonSecondary>
										<QeeColorButtonSecondary
											size="small"
											variant="outlined"
											startIcon={<ThumbDownAltOutlinedIcon />}
											onClick={openSupportCard}
											sx={{mr: 0}}
										>
											{labels.notHelpful}
										</QeeColorButtonSecondary>
									</Box>
								) : null}
							</Box>
						)}
					</Box>
					{message.avatar && message.self && ChatAvatar}
				</Box>
			</Box>
		</Grow>
	);
}
